import { useState, useEffect, useRef } from "react";
import Fab from "@mui/material/Fab";
import MicIcon from "@mui/icons-material/Mic";
import { Button } from "@mui/material";
import StopIcon from "@mui/icons-material/Stop";
import CancelIcon from "@mui/icons-material/Cancel";
import CircularProgress from "@mui/material/CircularProgress";
import SubmitIcon from "@mui/icons-material/CheckOutlined";
import { red } from "@mui/material/colors";

const TheAudioRecorder = ({
  onRecordingComplete,
  recordingDisabled,
  loading,
  setLoading,
  handleClear,
  setHelperText,
  setShowSuggestions,
  siteLanguage,
  translations,
  isPulsating,
  setIsPulsating,
  audioRef,
  setRecording,
  recording,
  handleRefresh,
  iosStatus,
  sendErrorToServer,
}) => {
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [remainingTime, setRemainingTime] = useState(30);
  const [cancelButton, setCancelButton] = useState("Cancel");
  const [submitButton, setSubmitButton] = useState("Submit");
  const [volume, setVolume] = useState(0);
  let animationFrameId = null;

  const mediaRecorder = useRef(null);
  const timerRef = useRef(null);
  const canceledRef = useRef(false);
  let stream = useRef(null);
  const hasRunOnce = useRef(false);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // console.log("Hi I am visible");
      } else {
        handleCancelRecording();
        if (stream.current) {
          stream.current.getTracks().forEach((track) => track.stop());
        }
        stream.current = null;
        mediaRecorder.current = null;
        // console.log(stream.current, mediaRecorder.current);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // Changes language of "Cancel" and "Submit" buttons
  useEffect(() => {
    setCancelButton(translations[siteLanguage].cancelButton);
    setSubmitButton(translations[siteLanguage].submitButton);
  }, [siteLanguage]);
  // If 30 seconds run out, auto triggers handleRecording() function
  useEffect(() => {
    if (remainingTime === 0 && recording) {
      handleRecording();
    }
  }, [remainingTime, recording]);

  useEffect(() => {
    // console.log("Recording state changed:", recording);
    if (recording) {
      // console.log("timerRef.current", timerRef.current);
      timerRef.current = setInterval(() => {
        setRemainingTime((prevTime) => (prevTime > 0 ? prevTime - 1 : 0));
      }, 1000);
    } else {
      setRemainingTime(30);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  }, [recording]);

  const handleRecording = async () => {
    handleClear();
    if (audioRef.current) {
      audioRef.current.pause();
    }

    if (recording && mediaRecorder.current) {
      // console.log("Stopping recording");
      // console.log("mediaRecorder.current", mediaRecorder.current);

      mediaRecorder.current.stop();
      setRecording(false);
    } else {
      // console.log("Starting recording");
      //   console.log("mediaRecorder.current", mediaRecorder.current);
      // console.log("BEFORE INIT STREAM", stream.current);
      // console.log(
      //   "BEFORE INIT STREAM getTracks()",
      //   stream.current?.getTracks()
      // );
      // console.log("Uh oh, no mediaRecorder.current....");
      // console.log("iosStatus", iosStatus);
      if (iosStatus && stream.current) {
        // console.log("isIos");
        stream.current.getTracks().forEach((track) => track.stop());
      }

      try {
        if (
          !stream.current ||
          !stream.current.active ||
          stream.current
            .getTracks()
            .some((track) => track.readyState !== "live" || track.muted)
        ) {
          stream.current = await navigator.mediaDevices.getUserMedia({
            audio: true,
          });

          stream.current.getTracks().forEach((track) => {
            track.addEventListener("ended", handleTrackEnded);
            track.addEventListener("errorrrrrr", (err) => console.log(err));
            track.onended = function (event) {
              // console.log("Track ended", event);
            };
          });
        }
      } catch (err) {
        // If the promise was rejected, that means the user denied permission
        // console.error("Error getting user media:", err);
        setPermissionDenied(true);
        console.log("THE MIC PERMISSION HAS BEEN DENIED");
        console.log(err);

        sendErrorToServer(err, { customMessage: "Mic Permission Denied" });
        alert("Microphone permission is required to record audio.");

        return; // Return early so the rest of the function doesn't execute
      }
      setShowSuggestions(false);
      setIsPulsating(false);

      const audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      const source = audioContext.createMediaStreamSource(stream.current);
      source.connect(analyser);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const animate = () => {
        analyser.getByteFrequencyData(dataArray);
        let values = 0;
        for (var i = 0; i < dataArray.length; i++) {
          values += dataArray[i];
        }
        const average = values / dataArray.length;
        setVolume(average);
        requestAnimationFrame(animate);
      };
      animate();
      // console.log("Before INIT MEDIARECORDER.CURRENT:", mediaRecorder.current);
      mediaRecorder.current = new MediaRecorder(stream.current);
      // console.log(
      //   "AFTER INIT MEDIARECORDER.CURRENT BEFORE PLAY:",
      //   mediaRecorder.current
      // );
      mediaRecorder.current.start();
      // console.log(
      //   "AFTER INIT MEDIARECORDER.CURRENT AFTER PLAY:",
      //   mediaRecorder.current
      // );

      setHelperText(translations[siteLanguage].isRecording);

      const audioChunks = [];
      mediaRecorder.current.ondataavailable = (event) => {
        // console.log("Data available from media recorder");
        audioChunks.push(event.data);
      };

      mediaRecorder.current.onstop = () => {
        // console.log("Recording stopped");
        mediaRecorder.current = null;
        // console.log(
        //   "MEDIARECORDER.CURRENT AFTER Nullification:",
        //   mediaRecorder.current
        // );
        cancelAnimationFrame(animationFrameId);
        audioContext.close();
        setIsPulsating(true);
        if (!canceledRef.current) {
          const audioBlob = new Blob(audioChunks, { type: "audio/wav" });

          const audioContext = new (window.AudioContext ||
            window.webkitAudioContext)();
          // console.log("audioContext", audioBlob, audioContext);
          if (audioBlob.size == 0) {
            return;
          }
          const reader = new FileReader();

          reader.onloadend = () => {
            const arrayBuffer = reader.result;
            audioContext.decodeAudioData(arrayBuffer, (buffer) => {
              const duration = buffer.duration;
              // console.log(`The duration is ${duration} seconds`);
              if (duration < 1) {
                // If duration is less than 0.5 seconds
                setRecording(false);

                // console.log("Recording is too short");
              } else {
                if (onRecordingComplete) {
                  // console.log("Calling onRecordingComplete");
                  onRecordingComplete(audioBlob);
                }
              }
            });
          };

          reader.readAsArrayBuffer(audioBlob);
        }
      };
      animationFrameId = requestAnimationFrame(animate);

      setRecording(true);
      canceledRef.current = false;
    }
  };

  const handleCancelRecording = () => {
    // console.log("Cancel recording");
    if (mediaRecorder.current && mediaRecorder.current.state !== "inactive") {
      mediaRecorder.current.stop();
    }
    setRecording(false);

    setHelperText(translations[siteLanguage].helperText);
    canceledRef.current = true;
  };

  function handleTrackEnded() {
    console.error("A MediaStreamTrack ended my dude. Not sure why...");
    // This function gets called when any track ends, so stop all tracks and reinitialize
    stream.current.getTracks().forEach((track) => {
      track.removeEventListener("ended", handleTrackEnded);
      track.stop();
    });
    // Here you might want to trigger the reinitialization of your recording process.
    handleCancelRecording();
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "20px",
        }}
      >
        {recording && (
          <>
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              onClick={handleCancelRecording}
              style={{ marginRight: "20px" }}
            >
              {cancelButton}
            </Button>
          </>
        )}
        <div
          style={{
            position: "relative",
            width: "90px",
            height: "90px",
            boxSizing: "border-box",
          }}
        >
          <Fab
            sx={{
              bgcolor: recording ? "error.main" : "primary.main",
              color: recording ? "white" : "primary.contrastText",
              animation: isPulsating ? "pulse 1.75s infinite" : "",
              boxShadow: isPulsating ? "0 0 0 rgba(44,12,90, 0.5)" : "",
              "&:hover": {
                bgcolor: recording ? "error.dark" : "primary.dark",
                // boxShadow: isPulsating
                //   ? "0 0 20px #2C3E50, 0 0 30px #2C3E50, 0 0 40px #2C3E50"
                //   : "",
              },
              "@keyframes pulse": {
                "0%": {
                  transform: "scale(1)",
                  // boxShadow: "0 0 0 0 rgba(0, 0, 0, 0.5)", // Starts with a light yellow shadow with 70% opacity
                },
                "50%": {
                  transform: "scale(1.2)",
                  // boxShadow: "0 0 0 20px rgba(0, 0, 0, 0.1)", // Grows larger to 40px and opacity reduces to 50%
                },

                "100%": {
                  transform: "scale(1.0)",
                  // boxShadow: "0 0 0 0 rgba(0, 0, 0, 0)", // Completely transparent again
                },
              },

              transform: recording ? `scale(${1 + volume / 255})` : "scale(1)",
              transition: "transform 0.1s ease-out",
            }}
            color={recording ? red[700] : "primary"}
            aria-label={recording ? "stop" : "start"}
            onClick={handleRecording}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              boxSizing: "border-box",
              margin: 0,
              padding: 0,
            }}
            disabled={loading || recordingDisabled}
          >
            {recording ? (
              <>
                <span
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    fontSize: "29px",
                    color: "white",
                  }}
                >
                  {remainingTime}
                </span>
              </>
            ) : (
              <MicIcon
                sx={{ color: isPulsating ? "white" : "white" }}
                style={{ fontSize: 65 }}
              />
            )}
          </Fab>
          {loading && (
            <CircularProgress
              variant="indeterminate"
              size={99}
              thickness={3}
              style={{
                position: "absolute",
                top: "-6%",
                left: "-4%",
                color: "#3f51b5",
              }}
            />
          )}
        </div>
        {recording && (
          <>
            <Button
              variant="outlined"
              color="success"
              endIcon={<SubmitIcon />}
              onClick={handleRecording}
              style={{ marginLeft: "20px" }}
            >
              {submitButton}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default TheAudioRecorder;
